/**
 * Operator-default maintenance interval — BULK management (Part 2 acceptance-6).
 *
 * The norm leaves VDMA maintenance frequency open by design; at TGA scale (Teile 2–7) per-item
 * setting is unworkable, so an operator sets defaults in bulk at gewerk / asset-type level (optionally
 * per taskType), overridable more specifically. This panel is that grid. No server work — it consumes
 * GET/PUT /maintenance-interval and the checklist definitions; the server resolver is authoritative.
 *
 * Guardrails mirrored in the UI: norm-derived intervals are FIXED facts (never settable to a looser
 * operator value); defaults are `pending` until confirmed and don't fire; bulk changes show their
 * impact count before applying; an open step renders "Intervall folgt", never a fabricated cadence.
 */
import { Fragment, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ChevronDown, ChevronRight, Loader2, MoveUp } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { regulatedObjectName } from "../../administration/operator-responsibility/legalDisplay";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Mirror of the server gewerkFor() grouping (tri-server assetConformity.ts). Kept in sync manually
// until a gewerk field exists on RegulatedObject.
const GEWERK_BY_CODE: Record<string, string> = {
  ventilationSystem: "rlt", garageVentilation: "rlt", pressureVentilation: "rlt",
  evaporativeCoolingSystem: "rlt", smokeExtractionSystem: "rlt",
  refrigerationSystem: "kaelte", pressureSystem: "kaelte",
  combustionSystem: "waerme", drinkingWaterInstallation: "sanitaer", safetyLighting: "elektro",
};
const gewerkFor = (code?: string) => (code ? GEWERK_BY_CODE[code] : undefined);

const isNormLocked = (iv: any): boolean =>
  !!iv && typeof iv === "object" && !!iv.months && (iv.intervalSource ?? "norm") === "norm";
const isOpen = (iv: any): boolean =>
  !!iv && typeof iv === "object" && iv.modus === "periodisch" && iv.frequencyResolved !== true && !isNormLocked(iv);

// The taskType select needs a non-empty value for "all" — Radix reserves "".
const ALL_TASK_TYPES = "__all__";

interface Setter {
  level: "gewerk" | "assetType";
  gewerk?: string;
  regulatedObjectCode?: string;
  label: string;
}

export default function MaintenanceIntervalPanel({ canWrite = true }: { canWrite?: boolean }) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const bindingAPI = useLegalBindingAPI(authentication?.customer);
  const adminAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [definitions, setDefinitions] = useState<any[]>([]);
  const [opDefaults, setOpDefaults] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [setter, setSetter] = useState<Setter | null>(null);
  const [months, setMonths] = useState("");
  const [taskType, setTaskType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const ints = await bindingAPI.getMaintenanceIntervals();
      setOpDefaults(ints || []);
    } catch (error) {
      pushError(error);
    }
    try {
      const defs = await adminAPI.getChecklistDefinitions();
      setDefinitions((defs || []).filter((d: any) => ["Wartung", "Inspektion"].includes(d.serviceType)));
    } catch (error) {
      // Admin API may not be accessible to tenant customer admins; definitions are
      // optional for viewing intervals, only required for setting new ones.
      setDefinitions([]);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  // Build the tree: gewerk → asset-type, with per-node open/norm-locked step counts and taskTypes.
  const tree = useMemo(() => {
    const byCode: Record<string, { items: any[]; taskTypes: Set<string> }> = {};
    for (const d of definitions) {
      const code = d.regulatedObjectCode;
      if (!code) continue;
      const node = (byCode[code] ??= { items: [], taskTypes: new Set() });
      for (const it of d.items || []) {
        node.items.push(it);
        if (isOpen(it.intervall) && it.taskType) node.taskTypes.add(it.taskType);
      }
    }
    const gewerke: Record<string, string[]> = {};
    for (const code of Object.keys(byCode)) {
      const gw = gewerkFor(code) || "sonstige";
      (gewerke[gw] ??= []).push(code);
    }
    const counts = (items: any[]) => ({
      open: items.filter((it) => isOpen(it.intervall)).length,
      norm: items.filter((it) => isNormLocked(it.intervall)).length,
      total: items.length,
    });
    return { byCode, gewerke, counts };
  }, [definitions]);

  // Operator default set AT an exact scope (for showing "set here" vs inherited).
  const defaultAt = (level: string, key: string, tt?: string) => {
    const matches = opDefaults.filter(
      (o) =>
        (o.level || (o.assetId ? "asset" : "assetType")) === level &&
        (level === "gewerk" ? o.gewerk === key : o.regulatedObjectCode === key),
    );
    if (tt !== undefined) {
      return matches.find((o) => o.taskType === tt);
    }
    return matches.find((o) => !o.taskType) || matches[0];
  };

  const monthsLabel = (m?: number) =>
    m ? t("maintenanceInterval.everyN", { count: m, defaultValue: "alle {{count}} Monate" }) : "";

  // Impact preview: open steps + asset-types a setter would affect (norm-locked steps are untouched).
  const impact = useMemo(() => {
    if (!setter) return { steps: 0, assetTypes: 0 };
    const codes =
      setter.level === "gewerk" ? tree.gewerke[setter.gewerk as string] || [] : [setter.regulatedObjectCode as string];
    let steps = 0;
    const types = new Set<string>();
    for (const code of codes) {
      const items = (tree.byCode[code]?.items || []).filter(
        (it) => isOpen(it.intervall) && (!taskType || it.taskType === taskType),
      );
      if (items.length) {
        steps += items.length;
        types.add(code);
      }
    }
    return { steps, assetTypes: types.size };
  }, [setter, taskType, tree]);

  const apply = async (confirm: boolean) => {
    if (!setter || !Number(months)) return;
    setSubmitting(true);
    try {
      await bindingAPI.setMaintenanceInterval({
        level: setter.level,
        gewerk: setter.level === "gewerk" ? setter.gewerk : undefined,
        regulatedObjectCode: setter.level === "assetType" ? setter.regulatedObjectCode : undefined,
        taskType: taskType || undefined,
        months: Number(months),
        confirm,
      });
      setSetter(null);
      setMonths("");
      setTaskType("");
      await load();
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (k: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const sourceBadge = (def: any) => {
    if (!def) return null;
    const pending = def.reviewStatus !== "confirmed";
    return (
      <Badge variant={pending ? "warning" : "success"} className="whitespace-normal text-left">
        {monthsLabel(def.months)} · {t("maintenanceInterval.operatorDefault", "Betreiber-Vorgabe")}
        {def.taskType ? ` (${def.taskType})` : ""} ·{" "}
        {pending ? t("maintenanceInterval.pending", "ausstehend") : t("maintenanceInterval.confirmed", "bestätigt")}
      </Badge>
    );
  };

  const setterRow = (s: Setter) => {
    const openTaskTypes = new Set<string>();
    const codes = s.level === "gewerk" ? tree.gewerke[s.gewerk as string] || [] : [s.regulatedObjectCode as string];
    codes.forEach((c) => tree.byCode[c]?.taskTypes.forEach((tt) => openTaskTypes.add(tt)));
    const monthsId = `${fieldId}-months`;
    const taskTypeId = `${fieldId}-task-type`;
    return (
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={5}>
          <div className="flex min-w-0 flex-wrap items-end gap-4">
            <b className="text-sm">
              {t("maintenanceInterval.setFor", "Vorgabe für")}: {s.label}
            </b>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={monthsId}>{t("maintenanceInterval.months", "Intervall (Monate)")}</Label>
              <Input
                id={monthsId}
                className="w-32"
                inputMode="numeric"
                value={months}
                onChange={(event) => setMonths((event.target.value || "").replace(/[^0-9]/g, ""))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={taskTypeId}>{t("maintenanceInterval.taskTypeOptional", "taskType (optional)")}</Label>
              <Select
                value={taskType || ALL_TASK_TYPES}
                onValueChange={(value: string) => setTaskType(value === ALL_TASK_TYPES ? "" : value)}
              >
                <SelectTrigger id={taskTypeId} className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TASK_TYPES}>{t("maintenanceInterval.allTaskTypes", "alle")}</SelectItem>
                  {Array.from(openTaskTypes).sort().map((tt) => (
                    <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="min-w-[220px] text-sm text-muted-foreground">
              {t("maintenanceInterval.impact", {
                steps: impact.steps, assetTypes: impact.assetTypes,
                defaultValue: "gilt für {{steps}} Schritte über {{assetTypes}} Anlagentypen",
              })}
            </span>
            <div className="flex flex-wrap gap-2 max-sm:w-full">
              <Button
                variant="outline"
                className="max-sm:flex-1"
                disabled={submitting || !Number(months)}
                onClick={() => apply(false)}
              >
                {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("maintenanceInterval.setPending", "Setzen (ausstehend)")}
              </Button>
              <Button
                className="max-sm:flex-1"
                disabled={submitting || !Number(months)}
                onClick={() => apply(true)}
              >
                {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("maintenanceInterval.setConfirm", "Setzen & bestätigen")}
              </Button>
              <Button variant="ghost" className="max-sm:flex-1" disabled={submitting} onClick={() => setSetter(null)}>
                {t("global.cancel", "Abbrechen")}
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-2">
        <h3 className="text-lg font-semibold">
          {t("maintenanceInterval.title", "Wartungsintervalle (Betreiber-Vorgaben)")}
        </h3>
        <p className="max-w-[720px] text-sm text-muted-foreground">
          {t("maintenanceInterval.intro",
            "Die Norm lässt die Wartungsfrequenz offen. Setzen Sie Vorgaben in Masse je Gewerk/Anlagentyp. " +
            "Norm-abgeleitete Intervalle sind feste Fakten und nicht auf einen schwächeren Wert setzbar. " +
            "Vorgaben sind ausstehend, bis sie bestätigt werden.")}
        </p>
      </div>
      {Object.keys(tree.gewerke).length === 0 ? (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm font-semibold text-foreground">
            {t("maintenanceInterval.empty", "Keine Wartungstiefe vorhanden.")}
          </p>
          <p className="mx-auto max-w-prose text-sm text-muted-foreground">
            {t("maintenanceInterval.emptyHint",
              "Sobald Checklisten mit Wartungs- oder Inspektionsschritten vorliegen, erscheinen hier " +
              "die Gewerke und Anlagentypen, für die sich eine Vorgabe setzen lässt.")}
          </p>
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("maintenanceInterval.scope", "Gewerk / Anlagentyp")}</TableHead>
                <TableHead className="w-32">{t("maintenanceInterval.openSteps", "offen")}</TableHead>
                <TableHead className="w-32">{t("maintenanceInterval.normLocked", "norm-fixiert")}</TableHead>
                <TableHead>{t("maintenanceInterval.effective", "Vorgabe")}</TableHead>
                <TableHead className="w-32">
                  <span className="sr-only">{t("maintenanceInterval.set", "Vorgabe")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.keys(tree.gewerke).sort().map((gw) => {
                const codes = tree.gewerke[gw];
                const gwItems = codes.flatMap((c) => tree.byCode[c]?.items || []);
                const c = tree.counts(gwItems);
                const gwDefault = defaultAt("gewerk", gw);
                const gwKey = `gw:${gw}`;
                const gwLabel = t(`gewerk.${gw}`, gw.toUpperCase());
                const gwExpanded = expanded.has(gwKey);
                return (
                  <Fragment key={gwKey}>
                    <TableRow className="font-semibold">
                      <TableCell className="min-w-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 gap-2 font-semibold"
                          aria-expanded={gwExpanded}
                          onClick={() => toggle(gwKey)}
                        >
                          {gwExpanded
                            ? <ChevronDown aria-hidden className="h-4 w-4" />
                            : <ChevronRight aria-hidden className="h-4 w-4" />}
                          <span className="min-w-0 break-words">{gwLabel}</span>
                        </Button>
                      </TableCell>
                      <TableCell className="tabular-nums">{c.open}</TableCell>
                      <TableCell className="tabular-nums">{c.norm}</TableCell>
                      <TableCell className="font-normal">
                        {gwDefault ? sourceBadge(gwDefault) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {c.open > 0 && canWrite && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSetter({ level: "gewerk", gewerk: gw, label: gw.toUpperCase() }); setMonths(""); setTaskType(""); }}
                          >
                            {t("maintenanceInterval.set", "Vorgabe")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {setter?.level === "gewerk" && setter.gewerk === gw && setterRow(setter)}
                    {gwExpanded &&
                      codes.sort().map((code) => {
                        const items = tree.byCode[code]?.items || [];
                        const cc = tree.counts(items);
                        const atDefault = defaultAt("assetType", code);
                        const label = regulatedObjectName(t, code);
                        return (
                          <Fragment key={`at:${code}`}>
                            <TableRow>
                              <TableCell className="min-w-0 break-words pl-10">{label}</TableCell>
                              <TableCell className="tabular-nums">{cc.open}</TableCell>
                              <TableCell className="tabular-nums">{cc.norm}</TableCell>
                              <TableCell>
                                {atDefault ? sourceBadge(atDefault)
                                  : gwDefault ? (
                                    <Badge
                                      variant={gwDefault.reviewStatus !== "confirmed" ? "warning" : "success"}
                                      className="gap-1 whitespace-normal text-left"
                                    >
                                      <MoveUp aria-hidden className="h-3 w-3 shrink-0" />
                                      {monthsLabel(gwDefault.months)} · {t("maintenanceInterval.inherited", "geerbt")} ·{" "}
                                      {gwDefault.reviewStatus !== "confirmed"
                                        ? t("maintenanceInterval.pending", "ausstehend")
                                        : t("maintenanceInterval.confirmed", "bestätigt")}
                                    </Badge>
                                  )
                                  : cc.open > 0 ? <span className="text-muted-foreground">{t("maintenance.intervalPending", "Intervall folgt")}</span>
                                  : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell>
                                {cc.open > 0 && canWrite && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setSetter({ level: "assetType", regulatedObjectCode: code, label }); setMonths(""); setTaskType(""); }}
                                  >
                                    {t("maintenanceInterval.set", "Vorgabe")}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                            {setter?.level === "assetType" && setter.regulatedObjectCode === code && setterRow(setter)}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
